import { useCallback, useMemo, useState } from 'react';

import { GridValidRowModel } from '@mui/x-data-grid';

import { navigate } from '@redwoodjs/router';
import { Metadata } from '@redwoodjs/web';

import DateFilterMenuItem from 'src/components/FilterDropDown/components/DateFilterMenuItem';
import {
    buildFilters,
    createFilterObject,
    DateRangeDetails,
} from 'src/components/FilterDropDown/utils';
import { NPIDataGrid, type NPIDataGridRow } from 'src/components/NPIDataGrid/NPIDataGrid';
import { CREDIT_NOTES_PAGINATED } from 'src/graphql/queries/creditNotes.query';
import useStablePagination from 'src/hooks/useStablePagination';
import useTable from 'src/hooks/useTable';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { resolvePageSize } from 'src/lib/pagination';
import CreditsTableColumnDef from 'src/pages/ViewCreditsPage/components/CreditsTableColumnDef';

const ViewCreditsPage = () => {
    const {
        rows,
        pagination,
        fetchMore,
        updateSorting,
        updateFilters,
        loading,
        sortState,
        filterState,
        reset,
    } = useTable({
        query: CREDIT_NOTES_PAGINATED,
        pagination: {
            page: 1,
            pageSize: resolvePageSize(),
        },
        sorting: [],
        filterModel: { items: [] },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    // Handle add credit
    const handleAddRecord = useCallback(() => {
        navigate('/finance/credits/add');
    }, []);

    const handleViewCredit = useCallback(({ id }: { id: number }) => {
        navigate(`/finance/credits/${id}`);
    }, []);

    const handleOnClearFilters = () => {
        setFromDate(null);
        setToDate(null);
        reset();
    };

    const handleOnApplyFilters = () => {
        const dateDetails: DateRangeDetails = {
            field: 'dateAdded',
            fromDate: fromDate || null,
            toDate: toDate || null,
        };
        const model = createFilterObject(buildFilters(dateDetails));
        updateFilters(model);
    };
    const { stableTotalRows, stablePage } = useStablePagination(pagination, loading);

    const columns = useMemo(() => {
        return CreditsTableColumnDef({ handleView: handleViewCredit });
    }, [handleViewCredit]);

    return (
        <PageContent heading="Credits" subHeading="View NPI Credits">
            <Metadata title="Credits" description="Credits Page" />

            <NPIDataGrid
                rows={rows as unknown as NPIDataGridRow[]}
                columns={columns}
                getRowId={(row: GridValidRowModel) => {
                    return row.id;
                }}
                title="Credits List"
                dataTestId="credits-list-data-table"
                key="credits-list-data-table"
                serverSideFiltering={{
                    onFilterChange: updateFilters,
                    filterModel: filterState,
                }}
                serverSideSorting={{
                    onSortChange: updateSorting,
                    sortModel: sortState,
                }}
                pagination={{
                    isEnabled: true,
                    mode: 'server',
                    page: stablePage - 1,
                    pageSize: resolvePageSize(pagination.pageSize),
                    totalRows: stableTotalRows,
                    onPageChange: (page: number, pageSize: number) => {
                        fetchMore({ page: page + 1, pageSize });
                    },
                }}
                addFilterButtonConfig={{
                    isFilterActive: fromDate !== null || toDate !== null,
                    handleOnClearFilters,
                    handleOnApplyFilters,
                    filters: [
                        <DateFilterMenuItem
                            key="credit-date-filter"
                            title="Date Added"
                            fromDate={fromDate}
                            toDate={toDate}
                            handleFromDateChange={setFromDate}
                            handleToDateChange={setToDate}
                            onClearFilter={handleOnClearFilters}
                        />,
                    ],
                }}
                addRecordButtonConfig={{
                    addRecordButtonText: 'Add Credit',
                    addRecordCallBack: handleAddRecord,
                }}
                handleReset={reset}
                filtering={{ isEnabled: true }}
                loading={loading}
            />
        </PageContent>
    );
};

export default ViewCreditsPage;
